import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Survey API is a Pages Function on the same origin (functions/api/survey.ts)
const API_BASE_URL = '';

const TOTAL_QUESTIONS = 8;

const Q1_OPTIONS = [
  '0–5%',
  '5–15%',
  '15–30%',
  '30–60%',
  '60%+',
  "We don't offer registration / don't track this",
];

const Q2_OPTIONS = [
  'Email (customer emails support team)',
  'Phone (customer calls in)',
  'Web form / ticket system',
  'ERP or CRM module',
  'Dedicated warranty management software',
  'No formal warranty claims process',
];

const Q3_OPTIONS = [
  'Low product registration rates',
  'High volume of support calls for basic issues',
  'Returns where nothing is wrong with the product ("no fault found")',
  "Can't track who owns which product (sold through retailers)",
  'Manual warranty claims taking too long',
  'Spare parts ordering is difficult for customers',
  'Meeting new regulations (DPP, Right to Repair)',
];

const Q4_OPTIONS = [
  'Under £10,000',
  '£10,000 – £50,000',
  '£50,000 – £200,000',
  '£200,000 – £500,000',
  'Over £500,000',
  "Don't know / prefer not to say",
];

const Q5_OPTIONS = [
  'Shopify / WooCommerce / ecommerce',
  'SAP / Oracle ERP',
  'Microsoft Dynamics / Business Central',
  'Sage 50 / Sage 200',
  'Xero / QuickBooks',
  'Salesforce / HubSpot CRM',
  'Custom / bespoke system',
  'Standalone would be fine',
];

const Q6_OPTIONS = [
  "Yes — we're actively exploring or implementing",
  'Yes — it came up but went nowhere',
  "Not yet, but we're interested",
  'No — not on our radar',
];

const Q7_OPTIONS = [
  'Auto-triage of warranty claims',
  'Fraud detection on warranty claims',
  'AI-generated customer communications and status updates',
  'Predictive defect analysis from claim patterns',
  'AI chatbot for customer self-service warranty queries',
  'None — not interested in AI for this area',
];

interface Answers {
  q1: string;
  q2: string;
  q3: string[];
  q4: string;
  q5: string[];
  q5_other: string;
  q6: string;
  q7: string[];
  q8: string;
  email: string;
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-8">
      <div className="flex justify-between text-sm mb-1" style={{ color: '#666' }}>
        <span>Question {current} of {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#e5e7eb' }}>
        <div
          className="h-2 rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, background: 'linear-gradient(135deg, #CFF710 0%, #00D084 100%)' }}
        />
      </div>
    </div>
  );
}

function RadioGroup({ options, value, onChange }: { options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-3">
      {options.map((opt) => (
        <label
          key={opt}
          className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 hover:border-green-400"
          style={{
            borderColor: value === opt ? '#00D084' : '#e5e7eb',
            backgroundColor: value === opt ? '#f0fdf4' : 'transparent',
          }}
        >
          <input
            type="radio"
            name="radio-group"
            checked={value === opt}
            onChange={() => onChange(opt)}
            className="sr-only"
          />
          <span
            className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
            style={{ borderColor: value === opt ? '#00D084' : '#d1d5db' }}
          >
            {value === opt && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#00D084' }} />}
          </span>
          <span className="text-sm" style={{ color: '#1a1a1a' }}>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({
  options, values, onChange, max,
}: { options: string[]; values: string[]; onChange: (v: string[]) => void; max?: number }) {
  const toggle = (opt: string) => {
    if (values.includes(opt)) {
      onChange(values.filter((v) => v !== opt));
    } else if (!max || values.length < max) {
      onChange([...values, opt]);
    }
  };

  return (
    <div className="space-y-3">
      {max && <p className="text-xs mb-2" style={{ color: '#888' }}>Select up to {max}</p>}
      {options.map((opt) => (
        <label
          key={opt}
          className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 hover:border-green-400"
          style={{
            borderColor: values.includes(opt) ? '#00D084' : '#e5e7eb',
            backgroundColor: values.includes(opt) ? '#f0fdf4' : 'transparent',
          }}
        >
          <input type="checkbox" checked={values.includes(opt)} onChange={() => toggle(opt)} className="sr-only" />
          <span
            className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
            style={{ borderColor: values.includes(opt) ? '#00D084' : '#d1d5db' }}
          >
            {values.includes(opt) && (
              <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#00D084" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="text-sm" style={{ color: '#1a1a1a' }}>{opt}</span>
        </label>
      ))}
    </div>
  );
}

export function SurveyPage() {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const industry = searchParams.get('industry') || '';
  const size = searchParams.get('size') || '';
  const source = searchParams.get('source') || 'direct';

  const [step, setStep] = useState(0); // 0 = intro, 1-8 = questions, 9 = submitting/done
  const [answers, setAnswers] = useState<Answers>({
    q1: '', q2: '', q3: [], q4: '', q5: [], q5_other: '', q6: '', q7: [], q8: '', email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const canProceed = (): boolean => {
    switch (step) {
      case 0: return true;
      case 1: return answers.q1 !== '';
      case 2: return answers.q2 !== '';
      case 3: return answers.q3.length > 0;
      case 4: return answers.q4 !== '';
      case 5: return answers.q5.length > 0;
      case 6: return answers.q6 !== '';
      case 7: return answers.q7.length > 0;
      case 8:
        if (answers.q8 === 'no') return true;
        if (answers.q8 === 'yes' && answers.email.includes('@')) return true;
        return answers.q8 !== '';
      default: return false;
    }
  };

  const submit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const body = {
        company,
        industry,
        size,
        source,
        q1_registration_rate: answers.q1,
        q2_warranty_process: answers.q2,
        q3_challenges: answers.q3.join('; '),
        q4_warranty_cost: answers.q4,
        q5_integrations: [...answers.q5, answers.q5_other ? `Other: ${answers.q5_other}` : ''].filter(Boolean).join('; '),
        q6_ai_discussion: answers.q6,
        q7_ai_interests: answers.q7.join('; '),
        q8_want_report: answers.q8,
        respondent_email: answers.email,
      };

      const res = await fetch(`${API_BASE_URL}/api/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Submission failed');
      }

      setIsSubmitted(true);
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const next = () => {
    if (step === 8) {
      submit();
    } else {
      setStep(step + 1);
    }
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  // Intro screen
  if (step === 0) {
    return (
      <SurveyShell>
        <div className="text-center max-w-lg mx-auto py-8">
          <div className="mb-6">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#f0fdf4', color: '#00B37E' }}>
              3-minute survey
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-4" style={{ color: '#241E61' }}>
            How are UK manufacturers thinking about AI in warranty &amp; returns?
          </h1>
          <p className="text-base mb-6" style={{ color: '#555' }}>
            We're building the <strong>UK Manufacturer Post-Purchase AI Report 2026</strong> — mapping how
            manufacturers handle warranty, returns, and customer support, and where AI fits.
          </p>
          {company && (
            <p className="text-sm mb-6 p-3 rounded-lg" style={{ backgroundColor: '#faf5ff', color: '#241E61' }}>
              We've pre-filled some context about <strong>{company}</strong> so you can skip generic questions.
            </p>
          )}
          <p className="text-sm mb-8" style={{ color: '#888' }}>
            8 questions. We'll send you the full report when published.
          </p>
          <button
            onClick={() => setStep(1)}
            className="rounded-md px-8 py-3 text-base font-bold shadow-lg transition-all duration-200 transform hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #CFF710 0%, #00D084 100%)', color: '#1a1a1a' }}
          >
            Start Survey
          </button>
        </div>
      </SurveyShell>
    );
  }

  // Thank you screen
  if (isSubmitted) {
    return (
      <SurveyShell>
        <div className="text-center max-w-lg mx-auto py-12">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #CFF710 0%, #00D084 100%)' }}>
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#241E61' }}>Thank you!</h2>
          <p className="text-base mb-2" style={{ color: '#555' }}>Your responses have been recorded.</p>
          {answers.q8 === 'yes' && (
            <p className="text-sm" style={{ color: '#888' }}>
              We'll send you the <strong>UK Manufacturer Post-Purchase AI Report 2026</strong> within 3 weeks.
            </p>
          )}
          <a
            href="https://brandedmark.com"
            className="inline-block mt-8 text-sm font-medium underline"
            style={{ color: '#00B37E' }}
          >
            Learn more about BrandedMark
          </a>
        </div>
      </SurveyShell>
    );
  }

  // Question steps
  return (
    <SurveyShell>
      <div className="max-w-lg mx-auto">
        <ProgressBar current={step} total={TOTAL_QUESTIONS} />

        {step === 1 && (
          <QuestionCard title="What percentage of your customers register their products after purchase?">
            <RadioGroup options={Q1_OPTIONS} value={answers.q1} onChange={(v) => setAnswers({ ...answers, q1: v })} />
          </QuestionCard>
        )}

        {step === 2 && (
          <QuestionCard title="How do you currently process warranty claims?">
            <RadioGroup options={Q2_OPTIONS} value={answers.q2} onChange={(v) => setAnswers({ ...answers, q2: v })} />
          </QuestionCard>
        )}

        {step === 3 && (
          <QuestionCard title="What are your biggest post-purchase challenges?">
            <CheckboxGroup options={Q3_OPTIONS} values={answers.q3} onChange={(v) => setAnswers({ ...answers, q3: v })} max={3} />
          </QuestionCard>
        )}

        {step === 4 && (
          <QuestionCard title="How much do you estimate warranty and returns cost your business annually?">
            <p className="text-xs mb-4" style={{ color: '#888' }}>Including staff time, logistics, replacements</p>
            <RadioGroup options={Q4_OPTIONS} value={answers.q4} onChange={(v) => setAnswers({ ...answers, q4: v })} />
          </QuestionCard>
        )}

        {step === 5 && (
          <QuestionCard title="Which systems would a post-purchase platform need to integrate with?">
            <CheckboxGroup options={Q5_OPTIONS} values={answers.q5} onChange={(v) => setAnswers({ ...answers, q5: v })} />
            <div className="mt-3">
              <input
                type="text"
                placeholder="Other (please specify)"
                value={answers.q5_other}
                onChange={(e) => setAnswers({ ...answers, q5_other: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
              />
            </div>
          </QuestionCard>
        )}

        {step === 6 && (
          <QuestionCard title="Has your leadership team discussed using AI for warranty, returns, or post-purchase support in the last 6 months?">
            <RadioGroup options={Q6_OPTIONS} value={answers.q6} onChange={(v) => setAnswers({ ...answers, q6: v })} />
          </QuestionCard>
        )}

        {step === 7 && (
          <QuestionCard title="Which AI applications in post-purchase operations interest you most?">
            <CheckboxGroup options={Q7_OPTIONS} values={answers.q7} onChange={(v) => setAnswers({ ...answers, q7: v })} max={3} />
          </QuestionCard>
        )}

        {step === 8 && (
          <QuestionCard title="Would you like a free copy of the full report when it's published?">
            <div className="space-y-3">
              <label
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 hover:border-green-400"
                style={{
                  borderColor: answers.q8 === 'yes' ? '#00D084' : '#e5e7eb',
                  backgroundColor: answers.q8 === 'yes' ? '#f0fdf4' : 'transparent',
                }}
              >
                <input type="radio" name="q8" checked={answers.q8 === 'yes'} onChange={() => setAnswers({ ...answers, q8: 'yes' })} className="sr-only" />
                <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: answers.q8 === 'yes' ? '#00D084' : '#d1d5db' }}>
                  {answers.q8 === 'yes' && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#00D084' }} />}
                </span>
                <span className="text-sm" style={{ color: '#1a1a1a' }}>Yes please</span>
              </label>
              <label
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 hover:border-green-400"
                style={{
                  borderColor: answers.q8 === 'no' ? '#00D084' : '#e5e7eb',
                  backgroundColor: answers.q8 === 'no' ? '#f0fdf4' : 'transparent',
                }}
              >
                <input type="radio" name="q8" checked={answers.q8 === 'no'} onChange={() => setAnswers({ ...answers, q8: 'no' })} className="sr-only" />
                <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: answers.q8 === 'no' ? '#00D084' : '#d1d5db' }}>
                  {answers.q8 === 'no' && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#00D084' }} />}
                </span>
                <span className="text-sm" style={{ color: '#1a1a1a' }}>No thanks</span>
              </label>
            </div>
            {answers.q8 === 'yes' && (
              <div className="mt-4">
                <input
                  type="email"
                  placeholder="Your email address"
                  value={answers.email}
                  onChange={(e) => setAnswers({ ...answers, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                />
                <p className="text-xs mt-1" style={{ color: '#888' }}>We'll send the report when published — nothing else.</p>
              </div>
            )}
          </QuestionCard>
        )}

        {error && (
          <div className="p-3 rounded-lg mb-4 text-sm" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
            {error}
          </div>
        )}

        <div className="flex justify-between mt-6">
          <button
            onClick={back}
            className="rounded-md px-5 py-2.5 text-sm font-medium transition-all duration-200"
            style={{ color: '#241E61', backgroundColor: '#f3f4f6' }}
          >
            Back
          </button>
          <button
            onClick={next}
            disabled={!canProceed() || isSubmitting}
            className="rounded-md px-6 py-2.5 text-sm font-bold shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
            style={{
              background: canProceed() ? 'linear-gradient(135deg, #CFF710 0%, #00D084 100%)' : '#e5e7eb',
              color: canProceed() ? '#1a1a1a' : '#999',
            }}
          >
            {isSubmitting ? 'Submitting...' : step === 8 ? 'Submit' : 'Next'}
          </button>
        </div>
      </div>
    </SurveyShell>
  );
}

function SurveyShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fafafa' }}>
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <a href="https://brandedmark.com" className="inline-block">
            <span className="text-xl font-bold" style={{ color: '#241E61' }}>BrandedMark</span>
          </a>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8" style={{ borderColor: '#e5e7eb' }}>
          {children}
        </div>
        <p className="text-center text-xs mt-6" style={{ color: '#aaa' }}>
          Your responses are confidential and used only for aggregated research.
        </p>
      </div>
    </div>
  );
}

function QuestionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-5" style={{ color: '#241E61' }}>{title}</h2>
      {children}
    </div>
  );
}
